import React, {
  useRef,
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { type Bag, ProductItem } from "@repo/apis/bag";

import { StockPriceData, getStockPriceData } from "@repo/apis/stockprice";
import { initialiseBagSdk } from "@repo/bag";
import type { BagSdkInstance, ExpandedBag } from "@repo/bag";
import { ChameleonRequest, is4XXError } from "@repo/chameleon-request";
import { useAuth } from "@repo/ui/auth";
import { useStore } from "@repo/ui/store";

type AddProduct = (params: {
  variantId: number;
  expand?: boolean;
}) => Promise<void>;
type RemoveProduct = (params: {
  bagItemId: string;
  expand?: boolean;
}) => Promise<void>;
type UpdateProduct = (params: {
  variantId: number;
  quantity: number;
  bagItemId: string;
  expand?: boolean;
}) => Promise<Awaited<ReturnType<BagSdkInstance["updateProduct"]>>["messages"]>;

export type BagItemLoadingStates = {
  isAddingProduct: boolean;
  isRemovingProduct: boolean;
  isUpdatingProduct: boolean;
  removingItemIds: Record<string, boolean>;
  updatingItemIds: Record<string, boolean>;
};

type BagSummary = {
  bagId: string | null;
  totalQuantity: number;
};

type GetProducts = () => ReturnType<BagSdkInstance["getProducts"]>;

type GetFreeDeliveryQualification = () => ReturnType<
  BagSdkInstance["getFreeDeliveryQualification"]
>;

type GetNextDayDelivery = () => ReturnType<
  BagSdkInstance["getNextDayDelivery"]
>;

type GetStockPrice = (productId: number) => StockPriceData | undefined;

export type BagItem = { id: string; productId: number; quantity: number };

export type BagContextState =
  | {
      status: "initialising" | "error";
      expandedBag: ExpandedBag | null;
      bagItems: BagItem[];
      totalQuantity: number;
      customerHasBag: boolean;
      bagItemLoadingStates: BagItemLoadingStates;
      isBagUpdating: boolean;
      addProduct: AddProduct;
      removeProduct: RemoveProduct;
      updateProduct: UpdateProduct;
      initialiseCheckout: null;
      initialiseFullBagData: null;
      getStockPrice: GetStockPrice;
      getNextDayDelivery: GetNextDayDelivery;
      getFreeDeliveryQualification: GetFreeDeliveryQualification;
      getProducts: GetProducts;
    }
  | {
      status: "ready";
      expandedBag: ExpandedBag | null;
      bagItems: BagItem[];
      totalQuantity: number;
      customerHasBag: boolean;
      bagItemLoadingStates: BagItemLoadingStates;
      isBagUpdating: boolean;
      addProduct: AddProduct;
      removeProduct: RemoveProduct;
      updateProduct: UpdateProduct;
      initialiseCheckout: BagSdkInstance["initialiseCheckout"];
      initialiseFullBagData: () => Promise<void>;
      getStockPrice: GetStockPrice;
      getNextDayDelivery: GetNextDayDelivery;
      getFreeDeliveryQualification: GetFreeDeliveryQualification;
      getProducts: GetProducts;
    };

type DeferredBagSdk = {
  promise: Promise<BagSdkInstance>;
  resolve: (sdk: BagSdkInstance) => void;
};

export type BagProviderProps = {
  children: React.ReactNode;
  chameleonRequestInstance: ChameleonRequest;
};

const defaultValue: BagContextState = {
  status: "initialising",
  expandedBag: null,
  bagItems: [],
  totalQuantity: 0,
  customerHasBag: false,
  initialiseCheckout: null,
  bagItemLoadingStates: {
    isAddingProduct: false,
    isRemovingProduct: false,
    isUpdatingProduct: false,
    removingItemIds: {},
    updatingItemIds: {},
  },
  isBagUpdating: false,
  addProduct: async () => {},
  removeProduct: async () => {},
  updateProduct: async () => [],
  initialiseFullBagData: null,
  getStockPrice: () => undefined,
  getNextDayDelivery: () => null,
  getFreeDeliveryQualification: () => null,
  getProducts: () => [],
};

const bagToSummary = (bag: Bag): BagSummary => ({
  bagId: bag.id,
  totalQuantity: bag.summary!.totalQuantity || 0,
});

const toBagItems = (items: Bag["items"] | undefined): BagItem[] =>
  (items ?? [])
    .filter((item): item is ProductItem => item.itemType === "Product")
    .map(({ id, productId, quantity }) => ({ id, productId, quantity }));

const BagContext = createContext<BagContextState>(defaultValue);

const defaultSummary: BagSummary = {
  bagId: null,
  totalQuantity: 0,
};

export const Provider = ({
  chameleonRequestInstance,
  children,
}: BagProviderProps) => {
  const [bagSdk, setBagSdk] = useState<BagSdkInstance | null>(null);
  const [status, setStatus] =
    useState<BagContextState["status"]>("initialising");
  const [expandedBag, setExpandedBag] = useState<ExpandedBag | null>(null);
  const [summary, setSummary] = useState<BagSummary>(defaultSummary);
  const [bagItems, setBagItems] = useState<BagItem[]>([]);
  const [bagLoadingStates, setBagLoadingStates] = useState<
    BagContextState["bagItemLoadingStates"]
  >(defaultValue.bagItemLoadingStates);
  const [stockPriceMap, setStockPriceMap] = useState<
    Record<number, StockPriceData>
  >({});
  const { status: authStatus, authClient } = useAuth();
  const { countryData, keyStoreDataversion, shop } = useStore();

  const deferredBagSdk = useRef<DeferredBagSdk | null>(null);

  if (!deferredBagSdk.current) {
    let resolver: DeferredBagSdk["resolve"] | null = null;

    const promise = new Promise<BagSdkInstance>((resolve) => {
      resolver = resolve;
    });

    deferredBagSdk.current = {
      promise,
      resolve: resolver!,
    };
  }

  useEffect(() => {
    if (authStatus === "initialising") {
      return;
    }

    initialiseBagSdk({
      chameleonRequestInstance,
      lang: countryData.defaultLanguage,
      keyStoreDataversion,
      store: countryData.storeCode,
      country: countryData.countryCode,
      originCountry: countryData.countryCode,
      sizeSchema: countryData.defaultSizeSchema,
      currency: countryData.defaultCurrency,
      shop,
      getIdTokenClaims: authClient.getIdTokenClaimsAsync,
      getAuthHeader: authClient.getAuthHeaderPayload,
      isAuthenticated: authClient.isAuthenticated,
    })
      .then(async (sdk) => {
        setBagSdk(sdk);
        setSummary({
          bagId: sdk.initialSummary.bagId,
          totalQuantity: sdk.initialSummary.totalQuantity,
        });
        setBagItems(sdk.initialSummary.items ?? []);
        deferredBagSdk.current?.resolve(sdk);
        setStatus("ready");
      })
      .catch(() => {
        setStatus("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryData,
    keyStoreDataversion,
    shop,
    chameleonRequestInstance,
    authStatus,
    authClient,
  ]);

  const isBagUpdating = useMemo(() => {
    return (
      bagLoadingStates.isAddingProduct ||
      bagLoadingStates.isRemovingProduct ||
      bagLoadingStates.isUpdatingProduct
    );
  }, [bagLoadingStates]);

  const initialiseFullBagData = useCallback(async () => {
    if (!bagSdk) return;

    try {
      const expandedBag = await bagSdk.getBag({ expand: true });
      setExpandedBag(expandedBag);
      setBagItems(toBagItems(expandedBag.items));
      setSummary(bagToSummary(expandedBag));

      const productIdsSet = expandedBag.items.reduce((productIds, item) => {
        if (item.itemType === "Product") {
          productIds.add(item.productId);
        }

        return productIds;
      }, new Set<number>());

      const productIds = Array.from(productIdsSet);

      if (!productIds.length) return;

      const data = await getStockPriceData({
        requestInstance: chameleonRequestInstance,
        productIds,
        keyStoreDataversion,
        store: countryData.storeCode,
        country: countryData.countryCode,
        lang: countryData.defaultLanguage,
        shop,
        sizeSchema: countryData.defaultSizeSchema,
      });

      const map = (data || []).reduce(
        (acc, sp) => {
          acc[sp.productId] = sp;
          return acc;
        },
        {} as Record<number, StockPriceData>,
      );

      setStockPriceMap(map);
      setStatus("ready");
    } catch (error: unknown) {
      setStockPriceMap({});
      setExpandedBag(null);
      setBagItems([]);
      setSummary(defaultSummary);

      if (is4XXError(error)) {
        return;
      }

      setStatus("error");
    }
  }, [
    bagSdk,
    chameleonRequestInstance,
    countryData.countryCode,
    countryData.defaultLanguage,
    countryData.defaultSizeSchema,
    countryData.storeCode,
    keyStoreDataversion,
    shop,
  ]);

  const addProduct: AddProduct = async ({ variantId, expand }) => {
    const sdk = await deferredBagSdk.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to add product to the bag. The Bag SDK did not initialise",
      );
    }

    setBagLoadingStates((prev) => ({ ...prev, isAddingProduct: true }));

    try {
      if (expand) {
        const updatedBag = await sdk.addProduct({
          variantId,
          expand: true,
        });

        setSummary(bagToSummary(updatedBag));
        setBagItems(toBagItems(updatedBag.items));
        setExpandedBag(updatedBag);
        setStatus("ready");
      } else {
        const updatedBag = await sdk.addProduct({
          variantId,
          expand: false,
        });

        setSummary(bagToSummary(updatedBag));
        setBagItems(toBagItems(updatedBag.items));
        setExpandedBag(null);
        setStatus("ready");
      }
    } finally {
      setBagLoadingStates((prev) => ({ ...prev, isAddingProduct: false }));
    }
  };

  const removeProduct: RemoveProduct = async ({ bagItemId, expand }) => {
    const sdk = await deferredBagSdk.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to remove product from the bag. The Bag SDK did not initialise",
      );
    }

    setBagLoadingStates((prev) => ({
      ...prev,
      isRemovingProduct: true,
      removingItemIds: { ...prev.removingItemIds, [bagItemId]: true },
    }));

    try {
      if (expand) {
        const updatedBag = await sdk.removeProduct({
          bagItemId,
          expand: true,
        });
        setSummary(bagToSummary(updatedBag));
        setBagItems(toBagItems(updatedBag.items));
        setExpandedBag(updatedBag);
        setStatus("ready");
        return;
      } else {
        const updatedBag = await sdk.removeProduct({
          bagItemId,
          expand: false,
        });
        setSummary(bagToSummary(updatedBag));
        setBagItems(toBagItems(updatedBag.items));
        setExpandedBag(null);
        setStatus("ready");
        return;
      }
    } finally {
      setBagLoadingStates((prev) => {
        const remainingIds = { ...prev.removingItemIds };
        delete remainingIds[bagItemId];
        return {
          ...prev,
          isRemovingProduct: Object.keys(remainingIds).length > 0,
          removingItemIds: remainingIds,
        };
      });
    }
  };

  const updateProduct: UpdateProduct = async ({
    bagItemId,
    variantId,
    quantity,
    expand,
  }) => {
    const sdk = await deferredBagSdk.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to update product in the bag. The Bag SDK did not initialise",
      );
    }

    setBagLoadingStates((prev) => ({
      ...prev,
      isUpdatingProduct: true,
      updatingItemIds: { ...prev.updatingItemIds, [bagItemId]: true },
    }));

    try {
      if (expand) {
        const updatedBag = await sdk.updateProduct({
          bagItemId,
          variantId,
          quantity,
          expand: true,
        });

        setSummary(bagToSummary(updatedBag.bag));
        setBagItems(toBagItems(updatedBag.bag.items));
        setExpandedBag(updatedBag.bag);
        setStatus("ready");

        return updatedBag.messages;
      } else {
        const updatedBag = await sdk.updateProduct({
          bagItemId,
          variantId,
          quantity,
          expand: false,
        });
        setSummary(bagToSummary(updatedBag.bag));
        setBagItems(toBagItems(updatedBag.bag.items));
        setExpandedBag(null);
        setStatus("ready");

        return updatedBag.messages;
      }
    } finally {
      setBagLoadingStates((prev) => {
        const remainingIds = { ...prev.updatingItemIds };
        delete remainingIds[bagItemId];

        return {
          ...prev,
          isUpdatingProduct: Object.keys(remainingIds).length > 0,
          updatingItemIds: remainingIds,
        };
      });
    }
  };

  const getNextDayDelivery = () => {
    if (!bagSdk || !expandedBag) return null;

    return bagSdk.getNextDayDelivery(expandedBag);
  };

  const getFreeDeliveryQualification = () => {
    if (!bagSdk || !expandedBag) return null;

    return bagSdk.getFreeDeliveryQualification(expandedBag);
  };

  const getProducts = () => {
    return bagSdk?.getProducts(expandedBag) || [];
  };

  const getStockPrice = (productId: number) => stockPriceMap[productId];
  const customerHasBag = !!summary.bagId;
  const totalQuantity = summary.totalQuantity;

  const contextValue: BagContextState =
    status === "ready" && bagSdk
      ? {
          status,
          expandedBag,
          bagItems,
          customerHasBag,
          totalQuantity,
          bagItemLoadingStates: bagLoadingStates,
          isBagUpdating,
          addProduct,
          removeProduct,
          updateProduct,
          initialiseCheckout: bagSdk.initialiseCheckout,
          initialiseFullBagData,
          getStockPrice,
          getNextDayDelivery,
          getFreeDeliveryQualification,
          getProducts,
        }
      : {
          status: status as "initialising" | "error",
          expandedBag,
          bagItems,
          customerHasBag,
          totalQuantity,
          bagItemLoadingStates: bagLoadingStates,
          isBagUpdating,
          addProduct,
          removeProduct,
          updateProduct: defaultValue.updateProduct,
          initialiseFullBagData: null,
          initialiseCheckout: null,
          getStockPrice,
          getNextDayDelivery,
          getFreeDeliveryQualification,
          getProducts,
        };

  return (
    <BagContext.Provider value={contextValue}>{children}</BagContext.Provider>
  );
};

Provider.displayName = "Bag.Provider";

export const useBag = () => useContext(BagContext);
